import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DOCUMENT_CATEGORIES, DOCUMENT_CATEGORY_LABELS, DOCUMENT_MAX_BYTES, formatBytes, type DocumentCategory, type DocumentItem, type DocumentReferenceKind, type Paginated } from '@kontor/shared';
import { api, apiUpload, ApiError, queryString } from '../api';
import { useSession } from '../session';
import { CollapsibleCard } from './CollapsibleCard';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const day = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });

/**
 * Dokumente zu einem Datensatz (Inventar, Fahrzeug …): Sie liegen im Modul Dokumente mit der Referenz auf den Datensatz und erscheinen dort ebenfalls. Der Bereich erscheint nur
 * mit dem Modul Dokumente und dem Leserecht.
 */
export function RecordDocuments({ referenceKind, referenceId, hint, defaultCategory = 'invoice', collapsible = false }: { referenceKind: DocumentReferenceKind; referenceId: string; hint?: string; defaultCategory?: DocumentCategory; /** Zugeklappt, bis man auf den Titel klickt (für Masken mit mehreren Bereichen) */ collapsible?: boolean }) {
  const { can, hasModule } = useSession();
  const client = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<DocumentCategory>(defaultCategory);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<{ name: string; ok: boolean; message: string }[]>([]);
  const enabled = hasModule('documents') && can('documents.read');
  const docs = useQuery({
    queryKey: ['documents', { referenceKind, referenceId }],
    queryFn: async () => (await api<Paginated<DocumentItem>>('GET', `/documents${queryString({ referenceKind, referenceId, perPage: 100, sort: '-createdAt' })}`)).data,
    enabled,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/documents/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['documents'] });
      client.invalidateQueries({ queryKey: ['storage'] });
    },
  });
  if (!enabled) return null;

  async function send(files: FileList | null) {
    const list = [...(files ?? [])];
    if (list.length === 0) return;
    setBusy(true);
    const out: { name: string; ok: boolean; message: string }[] = [];
    for (const file of list) {
      if (file.size > DOCUMENT_MAX_BYTES) {
        out.push({ name: file.name, ok: false, message: `Zu groß, erlaubt sind ${formatBytes(DOCUMENT_MAX_BYTES)}.` });
        continue;
      }
      try {
        await apiUpload(`/documents${queryString({ category, referenceKind, referenceId })}`, new File([file], file.name, { type: 'application/octet-stream' }));
        out.push({ name: file.name, ok: true, message: 'abgelegt' });
      } catch (e) {
        out.push({ name: file.name, ok: false, message: errorText(e, 'Das Hochladen hat nicht geklappt.') });
      }
    }
    setResults(out);
    setBusy(false);
    client.invalidateQueries({ queryKey: ['documents'] });
    client.invalidateQueries({ queryKey: ['storage'] });
  }

  const content = (
    <>
      <p className="muted small">{hint ? `${hint} ` : ''}Die Dateien erscheinen auch unter Dokumente.</p>
      {can('documents.write') && (
        <div className="record-upload">
          <label className="field">
            <span>Art</span>
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value as DocumentCategory)}>
              {DOCUMENT_CATEGORIES.map((c) => <option key={c} value={c}>{DOCUMENT_CATEGORY_LABELS[c]}</option>)}
            </select>
          </label>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={() => input.current?.click()}>{busy ? 'Wird hochgeladen …' : 'Dateien hochladen'}</button>
          <input ref={input} type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.docx,.xlsx,.pptx,.odt,.ods,.odp,.txt,.csv,.md" onChange={(e) => { void send(e.target.files); e.target.value = ''; }} />
        </div>
      )}
      {results.length > 0 && <ul className="doc-results" aria-live="polite">{results.map((r, i) => <li key={`${r.name}-${i}`} className={r.ok ? 'is-ok' : 'is-error'}><strong>{r.name}</strong>: {r.message}</li>)}</ul>}
      <ul className="record-docs">
        {docs.isLoading && <li className="muted">Wird geladen …</li>}
        {docs.data?.length === 0 && <li className="muted">Noch keine Dokumente.</li>}
        {docs.data?.map((d) => (
          <li key={d.id}>
            <a href={`/api/v1/documents/${d.id}/download`} className="strong-link">{d.title ?? d.fileName}</a> · {DOCUMENT_CATEGORY_LABELS[d.category]} · {formatBytes(d.sizeBytes)} · {day.format(new Date(d.createdAt))}
            {can('documents.delete') && <button type="button" className="link-button" disabled={remove.isPending} onClick={() => window.confirm(`„${d.title ?? d.fileName}“ endgültig löschen?`) && remove.mutate(d.id)}> Löschen</button>}
          </li>
        ))}
      </ul>
    </>
  );
  return collapsible ? (
    <CollapsibleCard title="Dokumente" storageKey={`docs-${referenceKind}`}>{content}</CollapsibleCard>
  ) : (
    <section className="card" aria-labelledby={`docs-${referenceKind}`}>
      <h2 id={`docs-${referenceKind}`}>Dokumente</h2>
      {content}
    </section>
  );
}
